export const RAPID_SWITCH_DEFAULTS = Object.freeze({
  snVinWindowSeconds: 30,
  vinSnWindowSeconds: 60,
  northAmericaThreshold: 9,
  otherThreshold: 4,
});

export const RAPID_SWITCH_LIMITS = Object.freeze({
  windowSeconds: Object.freeze({
    min: 1,
    max: 3600,
  }),
  threshold: Object.freeze({
    min: 2,
    max: 1000,
  }),
});

const BUSINESS_FIELDS = Object.freeze([
  'snVinWindowSeconds',
  'vinSnWindowSeconds',
  'northAmericaThreshold',
  'otherThreshold',
]);

const FIELD_RULES = Object.freeze({
  snVinWindowSeconds: {
    label: '同一 SN 切换 VIN 窗口',
    min: RAPID_SWITCH_LIMITS.windowSeconds.min,
    max: RAPID_SWITCH_LIMITS.windowSeconds.max,
  },
  vinSnWindowSeconds: {
    label: '同一 VIN 被多个 SN 访问窗口',
    min: RAPID_SWITCH_LIMITS.windowSeconds.min,
    max: RAPID_SWITCH_LIMITS.windowSeconds.max,
  },
  northAmericaThreshold: {
    label: '北美共享阈值',
    min: RAPID_SWITCH_LIMITS.threshold.min,
    max: RAPID_SWITCH_LIMITS.threshold.max,
  },
  otherThreshold: {
    label: '非北美共享阈值',
    min: RAPID_SWITCH_LIMITS.threshold.min,
    max: RAPID_SWITCH_LIMITS.threshold.max,
  },
});

function normalizeInteger(value, fallback, min, max) {
  const normalized = typeof value === 'number'
    ? value
    : Number(String(value ?? '').trim());

  if (!Number.isInteger(normalized) || normalized < min || normalized > max) {
    return fallback;
  }
  return normalized;
}

function normalizeBusinessConfig(payload, fallbacks) {
  const config = BUSINESS_FIELDS.reduce((normalized, field) => {
    const rule = FIELD_RULES[field];
    normalized[field] = normalizeInteger(
      payload?.[field],
      fallbacks[field],
      rule.min,
      rule.max
    );
    return normalized;
  }, {});

  if (config.northAmericaThreshold < config.otherThreshold) {
    config.northAmericaThreshold = fallbacks.northAmericaThreshold;
    config.otherThreshold = fallbacks.otherThreshold;
  }

  return config;
}

function normalizeEnabled(value) {
  if (typeof value === 'boolean') {
    return value;
  }
  return ['1', 'true', 'on'].includes(String(value ?? '').trim().toLowerCase());
}

export function normalizeRiskControlPayload(payload) {
  const body = payload?.data && typeof payload.data === 'object'
    ? payload.data
    : (payload || {});
  const defaults = normalizeBusinessConfig(
    body.defaults,
    RAPID_SWITCH_DEFAULTS
  );

  return {
    enabled: normalizeEnabled(body.enabled),
    ...normalizeBusinessConfig(body, RAPID_SWITCH_DEFAULTS),
    defaults,
  };
}

export function validateRiskControlDraft(draft) {
  for (const field of BUSINESS_FIELDS) {
    const rule = FIELD_RULES[field];
    const value = draft?.[field];

    if (!Number.isInteger(value)) {
      return `${rule.label}必须是整数`;
    }
    if (value < rule.min || value > rule.max) {
      return `${rule.label}必须在 ${rule.min} 到 ${rule.max} 之间`;
    }
  }

  if (draft.northAmericaThreshold < draft.otherThreshold) {
    return '北美共享阈值不得小于非北美共享阈值';
  }

  return '';
}

export function isRiskierChange(before, after) {
  return (
    after?.northAmericaThreshold < before?.northAmericaThreshold ||
    after?.otherThreshold < before?.otherThreshold
  );
}

export function toRiskControlRequest(draft) {
  return {
    snVinWindowSeconds: draft.snVinWindowSeconds,
    vinSnWindowSeconds: draft.vinSnWindowSeconds,
    northAmericaThreshold: draft.northAmericaThreshold,
    otherThreshold: draft.otherThreshold,
  };
}
